import { getSuggestedLayout, typeToFieldId } from '@components/Card/CardHeader/utilities';
import { areArraysEqual, generateEntityId, isValidUrl } from '@modules/helpers';
import { has } from '@modules/types';
import { isEmail } from '@modules/validators';
import produce from 'immer';
import { customAlphabet } from 'nanoid';
import type {
  AnonymousUser,
  Card,
  CardAddress,
  CardEmail,
  CardFields,
  CardMetadata,
  CardNameFields,
  CardPhoneNumber,
  CardPreset,
  CardPresetAddress,
  CardPresetEmail,
  CardPresetFields,
  CardPresetMetadata,
  CardPresetPhoneNumber,
  CardPresetUrl,
  CardUrl,
  CardUrlType,
  FieldTypes,
  MultiValuedField,
  User,
} from 'types';

const alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const nanoid = customAlphabet(alphabet, 12);

export const DEFAULT_CARD_COLOR = '#F45A57';
const DEFAULT_CARD_TITLE = 'Work';
const DEFAULT_CARD_PRESET_NAME = 'Company';

export const ALL_CARD_URL_FIELD_TYPES: CardUrlType[] = [
  'calendar',
  'calendly',
  'cashapp',
  'discord',
  'facebook',
  'github',
  'instagram',
  'link',
  'linkedin',
  'org',
  'paypal',
  'signal',
  'skype',
  'snapchat',
  'telegram',
  'tiktok',
  'twitch',
  'twitter',
  'venmo',
  'wechat',
  'whatsapp',
  'yelp',
  'youtube',
];

export const ALL_CARD_FIELD_TYPES = [
  'name',
  'org_name',
  'department',
  'job_title',
  'accreditations',
  'headline',
  'phone',
  'address',
  'email',
  ...ALL_CARD_URL_FIELD_TYPES,
];

export function generateFieldID(): string {
  return nanoid();
}

export function areAllCardFieldsEmpty(cardFields: CardFields): boolean {
  return Object.values(cardFields).every((x) => {
    if (Array.isArray(x)) {
      return x.length === 0;
    }
    return !x;
  });
}

export function extractCardFields(cardData: Card): CardFields {
  return {
    accreditations: cardData.accreditations,
    addresses: cardData.addresses,
    department: cardData.department,
    emails: cardData.emails,
    fieldOrder: cardData.fieldOrder,
    firstName: cardData.firstName,
    headline: cardData.headline,
    companyLogoImg: cardData.companyLogoImg,
    coverPhotoImg: cardData.coverPhotoImg,
    profilePictureImg: cardData.profilePictureImg,
    honorificPrefix: cardData.honorificPrefix,
    honorificSuffix: cardData.honorificSuffix,
    jobTitle: cardData.jobTitle,
    lastName: cardData.lastName,
    layout: cardData.layout,
    linkedAttributes: cardData.linkedAttributes,
    maidenName: cardData.maidenName,
    middleName: cardData.middleName,
    orgName: cardData.orgName,
    phoneNumbers: cardData.phoneNumbers,
    preferredName: cardData.preferredName,
    pronouns: cardData.pronouns,
    urls: cardData.urls,
  };
}

export function extractCardMetadata(cardData: Card): CardMetadata {
  return {
    cardId: cardData.cardId,
    cardEditId: cardData.cardEditId,
    cardUpdateId: cardData.cardUpdateId,
    cardTitle: cardData.cardTitle || '',
    orgId: cardData.orgId,
    userId: cardData.userId,
    profileId: cardData.profileId,
    referralLinkEnabled: cardData.referralLinkEnabled,
    blinqMessageEnabled: cardData.blinqMessageEnabled,
    whereWeMetEnabled: cardData.whereWeMetEnabled,
    isInvisible: cardData.isInvisible,
    isPaused: cardData.isPaused,
    color: cardData.color,
    creationTimestampMs: cardData.creationTimestampMs,
    updatedTimestampMs: cardData.updatedTimestampMs,
    cardPresetId: cardData.cardPresetId,
    recipientEmail: cardData.recipientEmail,
    referrerCardId: cardData.referrerCardId,
    referrerUserId: cardData.referrerUserId,
    bbActivationTimestampMs: cardData.bbActivationTimestampMs,
    createdBy: cardData.createdBy,
    layoutImagesLocked: cardData.layoutImagesLocked,
    logoIsInQr: cardData.logoIsInQr,
    emailSignature: cardData.emailSignature,
    logoInQrUrl: cardData.logoInQrUrl,
    cardIsEditable: cardData.cardIsEditable,
    lockedFields: cardData.lockedFields,
    lockedFieldTypes: cardData.lockedFieldTypes,
    allFieldTypesLocked: cardData.allFieldTypesLocked,
    lockedEmailSignatureFields: cardData.lockedEmailSignatureFields,
    allEmailSignatureFieldsLocked: cardData.allEmailSignatureFieldsLocked,
    colorLocked: cardData.colorLocked,
    qrCodeLocked: cardData.qrCodeLocked,
    activationEmailSentAt: cardData.activationEmailSentAt,
    userProfileId: cardData.userProfileId,
  };
}

export function extractCardPresetFields(cardPresetData: CardPreset): CardPresetFields {
  return {
    accreditations: cardPresetData.accreditations,
    addresses: cardPresetData.addresses,
    companyLogoImg: cardPresetData.companyLogoImg,
    coverPhotoImg: cardPresetData.coverPhotoImg,
    department: cardPresetData.department,
    emails: cardPresetData.emails,
    fieldOrder: cardPresetData.fieldOrder,
    headline: cardPresetData.headline,
    jobTitle: cardPresetData.jobTitle,
    layout: cardPresetData.layout,
    orgName: cardPresetData.orgName,
    phoneNumbers: cardPresetData.phoneNumbers,
    profilePictureImg: cardPresetData.profilePictureImg,
    urls: cardPresetData.urls,
  };
}

export function extractCardPresetMetadata(cardPresetData: CardPreset): CardPresetMetadata {
  return {
    cardPresetId: cardPresetData.cardPresetId,
    cardPresetName: cardPresetData.cardPresetName || '',
    orgId: cardPresetData.orgId,
    referralLinkEnabled: cardPresetData.referralLinkEnabled,
    blinqMessageEnabled: cardPresetData.blinqMessageEnabled,
    whereWeMetEnabled: cardPresetData.whereWeMetEnabled,
    isInvisible: cardPresetData.isInvisible,
    color: cardPresetData.color,
    creationTimestampMs: cardPresetData.creationTimestampMs,
    updatedTimestampMs: cardPresetData.updatedTimestampMs,
    presetEditable: cardPresetData.presetEditable,
    orgName: cardPresetData.orgName,
    logoIsInQr: cardPresetData.logoIsInQr,
    logoInQrUrl: cardPresetData.logoInQrUrl,
    cardIsEditable: cardPresetData.cardIsEditable,
    emailSignature: cardPresetData.emailSignature,
    lockedFields: cardPresetData.lockedFields,
    lockedFieldTypes: cardPresetData.lockedFieldTypes,
    allFieldTypesLocked: cardPresetData.allFieldTypesLocked,
    lockedEmailSignatureFields: cardPresetData.lockedEmailSignatureFields,
    allEmailSignatureFieldsLocked: cardPresetData.allEmailSignatureFieldsLocked,
    colorLocked: cardPresetData.colorLocked,
    qrCodeLocked: cardPresetData.qrCodeLocked,
    syncUpdates: cardPresetData.syncUpdates,
    layoutImagesLocked: cardPresetData.layoutImagesLocked,
  };
}

/**
 * Checks to see if a users input for a given field type is valid.
 * This method accepts all the card fields and an ID instead of the field value directly because the
 * name field is actually composed of many attributes.
 *
 * @param type The field type
 * @param cardFields The fields of the card we want to check
 * @param id The ID of the field we want to check the validity of
 */
export function isFieldValueValid<T extends CardFields | CardPresetFields>(
  type: Exclude<FieldTypes, 'url'>,
  cardFields: T,
  id: string | undefined,
): boolean {
  switch (type) {
    case 'name':
      return has(cardFields, 'firstName') && !!cardFields.firstName;
    case 'org_name':
      return true;
    case 'department':
      return true;
    case 'job_title':
      return true;
    case 'accreditations':
      return true;
    case 'headline':
      return true;
    case 'address':
      return true;
    case 'phone':
      // TODO: Add proper validation based on country code
      if (id === undefined) throw new Error('No index provided for validator');
      else if (!cardFields.phoneNumbers || cardFields.phoneNumbers.length === 0) return true;
      else {
        return !!cardFields.phoneNumbers.find(({ id: fieldId }) => fieldId === id)?.value;
      }
    case 'email':
      if (id === undefined) throw new Error('No index provided for validator');
      else if (!cardFields.emails || cardFields.emails.length === 0) return true;
      else {
        return isEmail(cardFields.emails.find(({ id: fieldId }) => fieldId === id)?.value || '');
      }
  }
}

/**
 * Construct the full name of a person (with formatting) given the attributes that make up the name.
 *
 * @param cardFields The card fields that contain all the attributes that make up the name field
 */
export function constructFullName(cardNameFields: CardNameFields): string {
  const {
    firstName,
    middleName,
    lastName,
    honorificPrefix,
    honorificSuffix,
    maidenName,
    preferredName,
  } = cardNameFields;

  let name = '';
  if (honorificPrefix) name += `${honorificPrefix} `;
  if (firstName) name += `${firstName}`;
  if (preferredName) name += ` "${preferredName}"`;
  if (middleName) name += ` ${middleName}`;
  if (lastName) name += ` ${lastName}`;
  if (honorificSuffix) name += ` ${honorificSuffix}`;
  if (maidenName) name += ` (${maidenName})`;

  return name;
}

export function getName({
  firstName,
  lastName,
}: {
  firstName?: string;
  lastName?: string;
}): string {
  if (firstName && lastName) return `${firstName} ${lastName}`;
  if (firstName) return firstName;
  if (lastName) return lastName;
  else return '';
}

export function getDefaultLabel(type: Exclude<FieldTypes, 'url'>): string {
  switch (type) {
    case 'phone':
      return 'Mobile';
    default:
      return '';
  }
}

export function getDefaultUrlValue(type: CardUrlType): string {
  switch (type) {
    case 'calendly':
      return 'calendly.com/';
    case 'cashapp':
      return '$';
    case 'discord':
      return 'discord.gg/';
    case 'facebook':
      return 'facebook.com/';
    case 'instagram':
    case 'threads':
      return '@';
    case 'linkedin':
      return 'linkedin.com/in/';
    case 'paypal':
      return 'paypal.me/';
    case 'tiktok':
      return '@';
    case 'twitch':
      return 'twitch.tv/';
    case 'twitter':
    case 'twitterX':
      return '@';
    case 'venmo':
      return '@';
    case 'yelp':
      return 'yelp.com/biz/';
    case 'youtube':
      return 'youtube.com/';
    default:
      return '';
  }
}

/**
 * Returns the placeholder text for each type of URL
 * @param type The type of URL field, e.g. twitter, instagram
 */
export function getDefaultUrlValueLabel(type: CardUrlType): string {
  switch (type) {
    case 'calendly':
      return 'URL';
    case 'cashapp':
      return 'Username/URL';
    case 'discord':
      return 'URL';
    case 'facebook':
      return 'Username/URL';
    case 'github':
      return 'Username/URL';
    case 'instagram':
    case 'threads':
      return 'Username/URL';
    case 'calendar':
    case 'link':
      return 'URL';
    case 'linkedin':
      return 'URL/Username';
    case 'company_url':
    case 'company':
    case 'org':
      return 'URL';
    case 'paypal':
      return 'URL';
    case 'signal':
      return '';
    case 'skype':
      return 'Username';
    case 'snapchat':
      return 'Username/URL';
    case 'telegram':
      return 'Username/URL';
    case 'tiktok':
      return 'Username/URL';
    case 'twitch':
      return 'Username/URL';
    case 'twitter':
    case 'twitterX':
      return 'Username/URL';
    case 'venmo':
      return 'Username/URL';
    case 'wechat':
      return '';
    case 'whatsapp':
      return '';
    case 'yelp':
      return 'URL';
    case 'youtube':
      return 'Channel/URL';
    case 'referral':
      return '';
    case 'promo':
      return '';
  }
}

/**
 * Checks to see if a users input for a given URL field type is valid
 * @param type The URL field type
 * @param userInput The users input for the given field type
 *
 */
// LinkedIn only allows letters, numbers and hyphens in a profile handle.
const LINKEDIN_HANDLE_PATTERN = /^[\p{L}\p{N}-]{3,100}$/u;
const LINKEDIN_SHORT_LINK_PATTERN = /^(?:https?:\/\/)?lnkd\.in\//i;

/** Decodes a URL path segment, or returns null when it holds a malformed escape. */
function decodeUrlSegment(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

/**
 * Whether a LinkedIn field value can point at a real profile. A URL on another host never
 * can, except an lnkd.in short link. Company pages carry no handle to check and pass through.
 */
function isValidLinkedInValue(userInput: string, state: URLFieldValueType): boolean {
  const input = userInput.trim();

  if (state === 'complete-unknown-url' || state === 'partial-unknown-url') {
    return LINKEDIN_SHORT_LINK_PATTERN.test(input);
  }

  const segment = state === 'shorthand' ? input : input.match(/linkedin\.com\/in\/([^/?#]*)/i)?.[1];

  if (segment === undefined) {
    return true;
  }

  const handle = decodeUrlSegment(segment);

  return handle !== null && LINKEDIN_HANDLE_PATTERN.test(handle);
}

export function isURLFieldValueValid(type: CardUrl['type'], userInput: string): boolean {
  const userInputState = getURLFieldValueState(type, userInput);

  if (type === 'linkedin' && !isValidLinkedInValue(userInput, userInputState)) {
    return false;
  }

  switch (userInputState) {
    case 'complete':
      return true;
    case 'partial':
      return true;
    case 'shorthand':
      switch (type) {
        case 'org':
        case 'calendar':
        case 'link':
          return false; // Company URLs and generic links don't accept shorthands since we don't know the hostname
        default:
          return true;
      }
    case 'complete-unknown-url':
      return true;
    case 'partial-unknown-url':
      return true;
    case 'invalid':
      return false;
    case 'incomplete':
      return false;
  }
}

/**
 * Create a fully formed URL from a users input
 * @param type The URL field type
 * @param userInput The users input for the given field type
 * @example hey -> https://twitter.com/hey
 * @example twitter.com/hey -> https://twitter.com/hey
 */
export function getURIFromURLFieldValue(
  type: CardUrl['type'],
  userInput: string,
): string | undefined {
  const userInputState = getURLFieldValueState(type, userInput);

  switch (userInputState) {
    case 'complete':
      switch (type) {
        case 'whatsapp':
          // We only do this for complete URLs because it seemed a bit silly to support a partial URL for a deprecated format
          if (userInput.startsWith('https://wa.me/')) {
            const match = userInput.match(/wa\.me\/([+\d()][\d\s()-]+[\d()])/);

            const number = match?.[1] || '';
            return `https://api.whatsapp.com/send?phone=${number.replace(/[\s()-+]/g, '')}`;
          } else return userInput;
        default:
          return userInput;
      }
    case 'partial-unknown-url':
      return `http://${userInput}`;
    case 'partial':
      switch (type) {
        case 'linkedin':
        case 'facebook':
        case 'youtube':
        case 'snapchat':
        case 'tiktok':
        case 'yelp':
        case 'paypal':
          if (userInput.startsWith('www')) return `https://${userInput}`;
          else return `https://www.${userInput}`;
        case 'calendar':
        case 'link':
        case 'org':
          // All secure sites will upgrade to https anyway so this at least supports sites that use http.
          return `http://${userInput}`;
        default:
          // We use https for any non generic link though to avoid a useless redirect.
          return `https://${userInput}`;
      }
    case 'shorthand':
      switch (type) {
        case 'org':
        case 'calendar':
        case 'link':
          return undefined; // Company URLs and generic links don't accept shorthands since we don't know the hostname
        default:
          return constructURIFromShorthand(type, userInput);
      }
    case 'complete-unknown-url':
      return userInput;
    case 'invalid':
      return undefined;
    case 'incomplete':
      return undefined;
  }
}

/**
 * Create suggestions for the field title based off the users input
 * @param type The URL field type
 * @param userInput The users input for the given field type
 */
export function getSuggestionsFromURLFieldValue(
  type: CardUrl['type'],
  userInput: string,
): string[] {
  const userInputState = getURLFieldValueState(type, userInput);

  switch (userInputState) {
    case 'complete':
      return extractSuggestionsFromURI(type, userInput);
    case 'partial':
      return extractSuggestionsFromURI(type, userInput);
    case 'shorthand':
      return constructSuggestionsFromShorthand(type, userInput);
    case 'complete-unknown-url':
      return [];
    case 'partial-unknown-url':
      return [];
    case 'invalid':
      return [];
    case 'incomplete':
      return [];
  }
}

/**
 * Given a complete or partial valid URI (e.g. https://twitter.com/hey) extract a set of suggestions for a field title.
 *
 * WARNING
 * We assume this is a complete or partially complete value, i.e. we do not check if the user input is a shorthand
 * or do any validation of the URI form/hostname. You should always call getURLFieldValueState to ensure that the user
 * input is a complete or partial URI before passing it to this method.
 * @param type The URL field type
 * @param uri A complete or partially complete valid URI for the given card type.
 */
function extractSuggestionsFromURI(type: CardUrl['type'], uri: string): string[] {
  switch (type) {
    case 'skype': {
      const match = uri.match(/^skype:(.+?)(?:\?call|\?chat)$/i);

      // The capture group will be the username, i.e. whatever was between skype: and ?call
      if (match) return [match[1]];
      else return [];
    }
    case 'company_url':
    case 'org':
    case 'calendar':
    case 'link': {
      // Extract the hostname and path
      const match = uri.match(/^(?:https?:\/\/)?(.+?)\/?(?:#|\?|$)/i);

      if (!match) return [];
      else return [match[1]];
    }
    default: {
      const match = uri.match(/^(?:.+?:\/\/)?.+?\/(.+?)\/?(?:#|\?|$)/i);

      if (!match) return [];

      // Pathname does not include the initial '/' OR the final '/' (if the pathname ended with a '/').
      // This way we can check if it is a root path or subpath by checking for the presence of a '/'
      const pathname = match[1];

      // e.g. a/b/c has 3 levels, whereas a has 1 level
      const numLevels = pathname.replace(/[^/]/g, '').length + 1;

      switch (type) {
        case 'twitter':
        case 'twitterX':
          // Profile
          if (numLevels === 1) return [`@${pathname}`];
          // Everything else
          else return [`twitter.com/${pathname}`];
        case 'instagram':
          // Profile
          if (numLevels === 1) return [`@${pathname}`];
          // Everything else
          else return [`instagram.com/${pathname}`];
        case 'threads':
          if (numLevels === 1) {
            return [`${pathname}`];
          }
          return [`threads.net/@${pathname}`];
        case 'tiktok':
          // A shareable link
          if (uri.includes('vm.tiktok.com/')) return [`vm.tiktok.com/${pathname}`];
          // A tag name
          else if (pathname.match(/^tag\/[^/]+$/)) {
            return [`#${pathname.split('tag/')[1]}`];
          }
          // Profile page
          else if (numLevels === 1) return [pathname];
          // Everything else
          else return [`tiktok.com/${pathname}`];
        case 'venmo':
          // Username
          if (pathname.startsWith('u/') && numLevels === 2) return [`@${pathname.split('u/')[1]}`];
          // Everything else
          else return [`venmo.com/${pathname}`];
        case 'cashapp':
          // Account name
          if (numLevels === 1) return [pathname];
          // Everything else
          else return [`cash.app/${pathname}`];
        case 'youtube':
          // Shareable video link
          if (uri.includes('youtu.be/')) return [`youtu.be/${pathname}`];
          // Channel name
          else if (pathname.startsWith('c/') && numLevels === 2) {
            return [`${pathname.split('c/')[1]}`];
          }
          // Channel ID
          else if (pathname.startsWith('channel/') && numLevels === 2) {
            return [`youtube.com/${pathname}`];
          }
          // Username
          else if (pathname.startsWith('user/') && numLevels === 2) {
            return [`${pathname.split('user/')[1]}`];
          }
          // Channel name
          else if (numLevels === 1) return [pathname];
          // Everything else
          else return [`youtube.com/${pathname}`];
        case 'linkedin':
          // Personal profile
          if (pathname.startsWith('in/') && numLevels === 2) return [`${pathname.split('in/')[1]}`];
          // Company profile (using the digit ID)
          else if (pathname.match(/^company\/\d+$/)) return [`linkedin.com/${pathname}`];
          // Company profile (using the human assigned ID)
          else if (pathname.startsWith('company/') && numLevels === 2) {
            return [`${pathname.split('company/')[1]}`];
          }
          // Everything else, e.g. posts
          else return [`linkedin.com/${pathname}`];
        case 'facebook':
          // Profile page
          if (numLevels === 1) return [pathname];
          // Everything else - Post, Video post
          else return [`facebook.com/${pathname}`];
        case 'whatsapp': {
          // api.whats.com link (this works reliably)
          if (uri.includes('api.whatsapp.com/')) {
            const phoneNumber = uri.split('=')[1];
            if (phoneNumber.startsWith('+')) return [phoneNumber];
            else return [`+${phoneNumber}`];
          }
          // wa.me link (we know this is a wa.me link because it's the only other link that is accepted)
          else return [pathname];
        }

        case 'snapchat':
          if (pathname.startsWith('add/') && numLevels == 2) {
            return [pathname.split('add/')[1]];
          } else return [`snapchat.com/${pathname}`];
        case 'github':
          // Organisation
          if (pathname.startsWith('orgs/') && numLevels == 2) {
            return [pathname.split('orgs/')[1]];
          }
          // Root of Account or Organisation
          else if (numLevels === 1) return [pathname];
          // Everything except root of Account or Organisation
          else return [`github.com/${pathname}`];
        case 'yelp':
          // Business page
          if (pathname.startsWith('biz/') && numLevels == 2) {
            return [pathname.split('biz/')[1]];
          }
          // Business photos page
          else if (pathname.startsWith('biz_photos/') && numLevels == 2) {
            return [pathname.split('biz_photos/')[1]];
          }
          // URLs like user profiles look really bad so it's better to just not offer a suggestion
          else return [];
        case 'paypal':
          // Payment link (from app)
          if (uri.includes('paypal.me/') && numLevels === 1) return [pathname];
          else if (uri.includes('paypal.me/')) return [`paypal.me/${pathname}`];
          // Payment link for the web
          else if (
            uri.includes('paypal.com/') &&
            pathname.startsWith('paypalme/') &&
            numLevels == 2
          ) {
            return [pathname.split('paypalme/')[1]];
          } else if (uri.includes('paypal.com/')) return [`paypal.com/${pathname}`];
          else return [];
        case 'calendly':
          // A username
          if (numLevels === 1) return [pathname];
          else if (numLevels === 2 && pathname.split('/')[1] === '15min') {
            return ['Book a 15 minute meeting with me'];
          } else if (numLevels === 2 && pathname.split('/')[1] === '30min') {
            return ['Book a 30 minute meeting with me'];
          } else if (numLevels === 2 && pathname.split('/')[1] === '60min') {
            return ['Book a 1 hour meeting with me'];
          }
          // Safeguard
          else return [];
        case 'discord':
          // All the extracted suggestions are server names
          if (uri.includes('discord.gg/') && numLevels === 1) return [pathname];
          else if (uri.includes('discord.gg/')) return [`discord.gg/${pathname}`];
          else if (
            uri.includes('discord.com/') &&
            pathname.startsWith('invite/') &&
            numLevels === 2
          ) {
            return [pathname.split('invite/')[1]];
          } else if (uri.includes('discord.com/')) return [`discord.com/${pathname}`];
          // Safeguard
          else return [];
        case 'twitch':
          // Channel name
          if (numLevels === 1) return [pathname];
          // Everything else
          else return [`twitch.tv/${pathname}`];
        case 'telegram':
          // Channel
          if (uri.includes('t.me/') && numLevels === 1) return [pathname];
          else if (uri.includes('t.me/')) return [`t.me/${pathname}`];
          else if (uri.includes('telegram.me/') && numLevels === 1) return [pathname];
          else if (uri.includes('telegram.me/')) return [`telegram.me/${pathname}`];
          // Safeguard
          else return [];
        case 'wechat':
          return [];
        case 'signal':
          // Phone number
          if (pathname.startsWith('#p/') && numLevels == 2) {
            return [pathname.split('#p/')[1]];
          }
          // Everything else
          else return [`signal.me/${pathname}`];
        default:
          return [];
      }
    }
  }
}

/**
 * Given a value that has been deemed to be a shorthand for the specified field type construct a set
 * of suggestions for the field title
 *
 * @param type The URL field type
 * @param shorthand User input that has been deemed to be a shorthand (using getURLFieldValueState)
 * @example constructSuggestionsFromShorthand("twitter", "hey") -> ["@hey"]
 */
function constructSuggestionsFromShorthand(type: CardUrl['type'], shorthand: string): string[] {
  switch (type) {
    case 'twitter':
    case 'twitterX':
    case 'instagram':
    case 'threads':
    case 'tiktok':
    case 'venmo':
      if (shorthand.startsWith('@')) return [shorthand];
      else return [`@${shorthand}`];
    case 'cashapp':
      if (shorthand.startsWith('$')) return [shorthand];
      else return [`$${shorthand}`];
    case 'youtube':
      if (shorthand.startsWith('UC')) return [`youtube.com/channel/${shorthand}`];
      else return [shorthand];
    default:
      return [shorthand];
  }
}

/**
 * Given a value that has been deemed to be a shorthand for the specified field type construct a valid URI
 *
 * @param type The URL field type
 * @param shorthand User input that has been deemed to be a shorthand (using getURLFieldValueState)
 * @example constructURIFromShorthand("twitter", "hey") -> "https://twitter.com/hey"
 */
function constructURIFromShorthand(type: CardUrl['type'], shorthand: string): string | undefined {
  switch (type) {
    case 'twitter':
    case 'twitterX':
      if (shorthand === '@') return undefined;
      if (shorthand.startsWith('@')) return `https://twitter.com/${shorthand.split(/@(.+)/)[1]}`;
      else return `https://twitter.com/${shorthand}`;
    case 'instagram':
      if (shorthand === '@') return undefined;
      if (shorthand.startsWith('@')) return `https://instagram.com/${shorthand.split(/@(.+)/)[1]}`;
      else return `https://instagram.com/${shorthand}`;
    case 'threads':
      if (shorthand === '@') return undefined;
      return `https://threads.net/${shorthand}`;
    case 'tiktok':
      if (shorthand === '@') return undefined;
      if (shorthand.startsWith('@')) return `https://www.tiktok.com/${shorthand}`;
      else return `https://www.tiktok.com/@${shorthand}`;
    case 'venmo':
      if (shorthand === '@') return undefined;
      if (shorthand.startsWith('@')) return `https://venmo.com/u/${shorthand.split(/@(.+)/)[1]}`;
      else return `https://venmo.com/u/${shorthand}`;
    case 'cashapp':
      if (shorthand === '$') return undefined;
      if (shorthand.startsWith('$')) return `https://cash.app/${shorthand}`;
      else return `https://cash.app/$${shorthand}`;
    case 'youtube':
      if (shorthand.startsWith('UC')) return `https://www.youtube.com/channel/${shorthand}`;
      else return `https://www.youtube.com/c/${shorthand}`;
    case 'skype':
      return `skype:${shorthand}?call`;
    case 'org':
      return undefined;
    case 'calendar':
    case 'link':
      return undefined;
    case 'linkedin':
      return `https://www.linkedin.com/in/${shorthand}`;
    case 'facebook':
      return `https://www.facebook.com/${shorthand}`;
    case 'whatsapp':
      return `https://api.whatsapp.com/send?phone=${shorthand.replace(/[\s()-+]/g, '')}`;
    case 'snapchat':
      return `https://www.snapchat.com/add/${shorthand}`;
    case 'github':
      return `https://github.com/${shorthand}`;
    case 'yelp':
      return `https://www.yelp.com/biz/${shorthand}`;
    case 'paypal':
      return `https://www.paypal.me/${shorthand}`;
    case 'calendly':
      return `https://calendly.com/${shorthand}`;
    case 'discord':
      return `https://discord.gg/${shorthand}`;
    case 'twitch':
      return `https://twitch.tv/${shorthand}`;
    case 'telegram':
      return `https://telegram.me/${shorthand}`;
    case 'wechat':
      return undefined;
    case 'signal':
      return `https://signal.me/#p/${shorthand}`;
    case 'promo':
      return undefined;
    case 'referral':
      return undefined;
  }
}

type URLFieldValueType =
  | 'complete'
  | 'partial'
  | 'shorthand'
  | 'incomplete'
  | 'invalid'
  | 'complete-unknown-url'
  | 'partial-unknown-url';

/**
 * Get the state of a URL field value entered by the user. This can be one of the following:
 * - Complete - The value is a fully formed URI from a domain we recognize
 * - Partial - The value is a valid URL from a domain we recognize that is missing a protocol (e.g. http or https)
 * - Shorthand - The value does not resemble a URI and it is a valid set of characters (e.g. hey)
 * - Incomplete - The value is neither a valid URI for the given field type or a shorthand as it has yet to be completed (e.g. skype: OR https://)
 * - Invalid - The value contains illegal characters that make it impossible to be a valid URI or shorthand
 *
 * @param type The URL field type
 * @param userInput The users input for the given field type
 */
export function getURLFieldValueState(type: CardUrl['type'], userInput: string): URLFieldValueType {
  if (!userInput) return 'incomplete';

  switch (type) {
    case 'skype':
      // Skype does not use the http scheme, it uses the skype scheme (in order to directly open the Skype application)
      if (/^skype:.+?(?:\?call|\?chat)$/.test(userInput)) return 'complete';
      else if (/^skype:.*$/.test(userInput)) return 'incomplete';
      else return 'shorthand';
    case 'company_url':
    case 'company':
    case 'org':
    case 'calendar':
    case 'link':
      // For company URLs and generic links we accept any valid URL, regardless of the hostname
      if (isCompleteURL(userInput)) return 'complete';
      else if (isPartialURL(userInput)) {
        // If we have at least a hostname than it's a partially complete URL
        return 'partial';
      } else return 'incomplete';
    case 'whatsapp':
      // WhatsApp links are only valid if the correct pathname and query params are used (if using api.whatsapp.com)
      if (
        /^https?:\/\/api\.whatsapp\.com\/send\?(phone|text)=.+$/.test(userInput) ||
        /^https?:\/\/wa\.me\/.+$/.test(userInput)
      ) {
        return 'complete';
      } else if (
        /^api\.whatsapp\.com\/send\?(phone|text)=.+$/.test(userInput) ||
        /^wa\.me\/.+$/.test(userInput)
      ) {
        return 'partial';
      } else if (/^https?:\/\/(?:.*)?$/.test(userInput)) {
        return 'incomplete';
      } else return 'shorthand';
    default:
      return getURLFieldValueWithKnownHostnameState(type, userInput);
  }
}

export function isCompleteURL(userInput: string): boolean {
  return /^https?:\/\/.+$/.test(userInput);
}

export function isPartialURL(userInput: string): boolean {
  return /^(([a-zA-Z\d]|[a-zA-Z\d][a-zA-Z\d-]*[a-zA-Z\d])\.)+([A-Za-z\d]|[A-Za-z\d][A-Za-z\d-]*[A-Za-z\d])(\/.*)?$/.test(
    userInput,
  );
}

/**
 * Get the state of a URL field value entered by the user. This can be one of the following:
 * - Complete - The value contains a valid protocol, hostname and the pathname exists
 * - Partial - The value contains a valid hostname and the pathname exists
 * - Incomplete - The value has a valid protocol but does not contain both a valid hostname and a non-empty pathname OR the value has a valid hostname but no pathname exists
 * - Shorthand - The value is just a regular string
 *
 * A hostname is valid if it corresponds to one of the accepted hostnames for a given URL type
 *
 * WARNING
 * This should only be used on URL field types where the value of the pathname or query parameters do not affect
 * whether the user input is a valid URI. E.g. Twitter - as long as the hostname (twitter.com) is present after
 * the protocol and a pathname is present than it is valid, whereas some WhatsApp links are only valid if the
 * pathname and query params are correct.
 *
 * @param type The URL field type
 * @param userInput The users input for the given field type
 */
function getURLFieldValueWithKnownHostnameState(
  type: CardUrl['type'],
  userInput: string,
): URLFieldValueType {
  // These are the root domains that are recognized as being valid for the given field type. We don't bother
  // validating subdomains as it becomes too brittle.
  const acceptedDomains = getAcceptedDomainsByFieldType(type);

  if (new RegExp(`^https?://(?:.*?\\.)?(?:${acceptedDomains.join('|')})/.+$`).test(userInput)) {
    // User input is a complete URL with a valid hostname and pathname (e.g. https://twitter.com/hey)
    return 'complete';
  } else if (
    new RegExp(`^https?://(?:.*?\\.)?(?:${acceptedDomains.join('|')})/?$`).test(userInput)
  ) {
    // User input is a complete URL with a valid hostname but no pathname (e.g. https://twitter.com)
    return 'incomplete';
  } else if (new RegExp(`^https?://$`).test(userInput)) {
    // User input includes a protocol (e.g. https) but does not include a valid hostname (e.g. https://tw)
    return 'incomplete';
  } else if (new RegExp(`^(?:.*?\\.)?(?:${acceptedDomains.join('|')})/.+$`).test(userInput)) {
    // User input does not include a protocol (e.g. https) but does include a valid hostname and pathname (e.g. twitter.com/hey)
    return 'partial';
  } else if (new RegExp(`^(?:.*?\\.)?(?:${acceptedDomains.join('|')})/?$`).test(userInput)) {
    // User input does not include a protocol (e.g. https) or pathname but does include a valid hostname (e.g. twitter.com/)
    return 'incomplete';
  } else if (isCompleteURL(userInput)) {
    return 'complete-unknown-url';
  } else if (isPartialURL(userInput)) {
    return 'partial-unknown-url';
  } else return 'shorthand'; // User input is just a string
}

// const ALL_ACCEPTED_DOMAINS = [
//     "twitter.com",
//     "instagram.com",
//     "linkedin.com",
//     "facebook.com",
//     "youtube.com",
//     "youtu.be",
//     "whatsapp.com",
//     "wa.me",
//     "snapchat.com",
//     "tiktok.com",
//     "github.com",
//     "yelp.com",
//     "venmo.com",
//     "paypal.me",
//     "paypal.com",
//     "cash.app",
//     "calendly.com",
//     "discord.com",
//     "discord.gg",
//     "twitch.tv",
//     "telegram.me",
//     "t.me",
//     "signal.me",
// ];

// function getAcceptedDomains(exceptType?: CardUrl["type"]): string[] {
//     if (!exceptType) return ALL_ACCEPTED_DOMAINS;

//     const domainsToExclude = getAcceptedDomainsByFieldType(exceptType);

//     return ALL_ACCEPTED_DOMAINS.filter((domain) => {
//         return !domainsToExclude.includes(domain);
//     });
// }

/**
 * Return the set of root domains that we recognize as being valid for the given field type.
 *
 * @param type The URL field type
 */
function getAcceptedDomainsByFieldType(type: CardUrl['type']): string[] {
  switch (type) {
    case 'company_url':
    case 'company':
    case 'org':
      return [];
    case 'calendar':
    case 'link':
      return [];
    case 'twitter':
    case 'twitterX':
      return ['twitter.com'];
    case 'instagram':
      return ['instagram.com'];
    case 'threads':
      return ['threads.net'];
    case 'linkedin':
      return ['linkedin.com'];
    case 'facebook':
      return ['facebook.com'];
    case 'youtube':
      return ['youtube.com', 'youtu.be'];
    case 'whatsapp':
      return ['whatsapp.com', 'wa.me'];
    case 'snapchat':
      return ['snapchat.com'];
    case 'tiktok':
      return ['tiktok.com'];
    case 'github':
      return ['github.com'];
    case 'yelp':
      return ['yelp.com'];
    case 'venmo':
      return ['venmo.com'];
    case 'paypal':
      return ['paypal.me', 'paypal.com', 'paypal.biz'];
    case 'cashapp':
      return ['cash.app'];
    case 'calendly':
      return ['calendly.com'];
    case 'discord':
      return ['discord.com', 'discord.gg'];
    case 'twitch':
      return ['twitch.tv'];
    case 'telegram':
      return ['telegram.me', 't.me'];
    case 'skype':
      return [];
    case 'wechat':
      return [];
    case 'signal':
      return ['signal.me'];
    case 'referral':
      return [];
    case 'promo':
      return [];
  }
}

export function getSuggestionsByFieldType(
  type: Extract<FieldTypes, 'url'>,
): typeof getURLSuggestionsByFieldType;
export function getSuggestionsByFieldType(
  type: Extract<FieldTypes, 'phone' | 'email' | 'address'>,
): ReturnType<typeof getNonURLSuggestionsByFieldType>;
export function getSuggestionsByFieldType(
  type: Extract<FieldTypes, 'phone' | 'email' | 'address' | 'url'>,
): typeof getURLSuggestionsByFieldType | ReturnType<typeof getNonURLSuggestionsByFieldType> {
  switch (type) {
    case 'url':
      return getURLSuggestionsByFieldType;
    default:
      return getNonURLSuggestionsByFieldType(type);
  }
}

const getNonURLSuggestionsByFieldType =
  (type: Extract<FieldTypes, 'phone' | 'email' | 'address'>) => (): string[] => {
    switch (type) {
      case 'phone':
        return ['Cell', 'Mobile', 'Work', 'Home'];
      case 'email':
        return ['Work', 'Personal'];
      case 'address':
        return ['Office Address', 'Work Address', 'Home Address', 'Mailing Address'];
    }
  };

export function getURLSuggestionsByFieldType(type: CardUrl['type']): string[] {
  let suggestions: string[] = [];

  switch (type) {
    case 'company_url':
    case 'org':
      suggestions = ['Visit our website'];
      break;
    case 'calendar':
      suggestions = ['Book a meeting', 'Schedule a call'];
      break;
    case 'link':
      suggestions = [];
      break;
    case 'twitter':
    case 'twitterX':
      suggestions = ['Follow me on X'];
      break;
    case 'instagram':
      suggestions = ['Follow me on Instagram'];
      break;
    case 'linkedin':
      suggestions = ['Connect with me on LinkedIn'];
      break;
    case 'facebook':
      suggestions = ['Friend me on Facebook'];
      break;
    case 'youtube':
      suggestions = ['Subscribe to my channel on YouTube'];
      break;
    case 'whatsapp':
      suggestions = ['Add me on WhatsApp', 'Connect with me on WhatsApp'];
      break;
    case 'snapchat':
      suggestions = ['Add me on Snapchat', 'Send me a snap'];
      break;
    case 'tiktok':
      suggestions = ['Follow me on TikTok'];
      break;
    case 'github':
      suggestions = ['View our work on GitHub', 'View our GitHub Repo'];
      break;
    case 'yelp':
      suggestions = ['View our business on Yelp'];
      break;
    case 'venmo':
      suggestions = ['Pay via Venmo'];
      break;
    case 'paypal':
      suggestions = ['Pay via PayPal'];
      break;
    case 'cashapp':
      suggestions = ['Pay via CashApp'];
      break;
    case 'calendly':
      suggestions = ['Book a session with me', 'Book a meeting with me'];
      break;
    case 'discord':
      suggestions = ['Join our Discord server'];
      break;
    case 'twitch':
      suggestions = ['Follow me on Twitch'];
      break;
    case 'telegram':
      suggestions = ['Connect with me on Telegram'];
      break;
    case 'skype':
      suggestions = ['Call me on Skype', 'Skype with me'];
      break;
    case 'wechat':
      suggestions = ['Message me on WeChat'];
      break;
    case 'signal':
      suggestions = ['Add me on Signal', 'Connect with me on Signal'];
      break;
  }

  return suggestions;
}

/**
 * Combines the accreditations from a preset and the card the preset is being applied to. This will remove
 * any duplicates that may exist.
 * @param presetAccreditations The preset accreditations
 * @param cardAccreditations The accreditations on the card which is having a preset applied to it
 */
export function getAccreditationsAfterApplyingPreset(
  presetAccreditations: string[] | undefined,
  cardAccreditations: string[] | undefined,
): string[] {
  const newAccreditations = new Set<string>();

  if (presetAccreditations) {
    for (const accreditation of presetAccreditations) {
      newAccreditations.add(accreditation);
    }
  }

  if (cardAccreditations) {
    for (const accreditation of cardAccreditations) {
      newAccreditations.add(accreditation);
    }
  }

  return Array.from(newAccreditations);
}

/**
 * Remove a preset from a card by removing or resetting any field inherited by the preset.
 * This involves removing any multi valued preset fields from a card, resetting any single
 * valued fields, and removing the locks and links on these fields.
 * @param card The card that will be updated by a SCIM User Resource
 * @returns The card ready to be synced with a SCIM User Resource
 */
export function removePresetFromCard(card: Card, cardPreset: CardPreset): Card {
  const fieldsToRemove = new Set<string>();

  if (cardPreset.jobTitle === card.jobTitle) fieldsToRemove.add('job_title');
  if (cardPreset.orgName === card.orgName) fieldsToRemove.add('org_name');
  if (cardPreset.department === card.department) fieldsToRemove.add('department');
  if (cardPreset.headline === card.headline) fieldsToRemove.add('headline');
  if (areArraysEqual(cardPreset.accreditations, card.accreditations)) {
    fieldsToRemove.add('accreditations');
  }
  if (cardPreset.color === card.color) fieldsToRemove.add('color');

  const profilePictureMatchesPreset =
    card.profilePictureImg?.editedImageUrl === cardPreset?.profilePictureImg?.editedImageUrl;
  const logoPictureMatchesPreset =
    card.companyLogoImg?.editedImageUrl === cardPreset?.companyLogoImg?.editedImageUrl;
  const coverPhotoMatchesPreset =
    card.coverPhotoImg?.editedImageUrl === cardPreset?.coverPhotoImg?.editedImageUrl;

  if (profilePictureMatchesPreset) fieldsToRemove.add(typeToFieldId('profile'));
  if (logoPictureMatchesPreset) fieldsToRemove.add(typeToFieldId('logo'));
  if (coverPhotoMatchesPreset) fieldsToRemove.add(typeToFieldId('cover'));
  if (cardPreset.lockedFields?.includes('layout')) fieldsToRemove.add('layout');

  card.emails?.forEach(({ id, isPreset }) => {
    if (isPreset && id) fieldsToRemove.add(id);
  });
  card.phoneNumbers?.forEach(({ id, isPreset }) => {
    if (isPreset && id) fieldsToRemove.add(id);
  });
  card.urls?.forEach(({ id, isPreset }) => {
    if (isPreset && id) fieldsToRemove.add(id);
  });
  card.addresses?.forEach(({ id, isPreset }) => {
    if (isPreset && id) fieldsToRemove.add(id);
  });

  // We don't need to do anything with the linked attributes since attributes that were inherited from
  // a preset are by definition not linked to a user profile. For non-complex attributes we only remove
  // the value if the values on the card and card preset are the same. For locks we reset any locks that
  // the preset has applied.
  const updatedCard: Card = {
    ...card,
    cardPresetId: null,
    jobTitle: fieldsToRemove.has('job_title') ? '' : card.jobTitle,
    orgName: fieldsToRemove.has('org_name') ? '' : card.orgName,
    department: fieldsToRemove.has('department') ? '' : card.department,
    headline: fieldsToRemove.has('headline') ? '' : card.headline,
    accreditations: fieldsToRemove.has('accreditations') ? [] : card.accreditations,
    profilePictureImg: fieldsToRemove.has(typeToFieldId('profile')) ? null : card.profilePictureImg,
    companyLogoImg: fieldsToRemove.has(typeToFieldId('logo')) ? null : card.companyLogoImg,
    coverPhotoImg: fieldsToRemove.has(typeToFieldId('cover')) ? null : card.coverPhotoImg,
    emails: card.emails?.filter(({ id }) => id && !fieldsToRemove.has(id)),
    phoneNumbers: card.phoneNumbers?.filter(({ id }) => id && !fieldsToRemove.has(id)),
    urls: card.urls?.filter(({ id }) => id && !fieldsToRemove.has(id)),
    addresses: card.addresses?.filter(({ id }) => id && !fieldsToRemove.has(id)),
    lockedFields: card.lockedFields?.filter((id) => !fieldsToRemove.has(id)),
    lockedFieldTypes: card.lockedFieldTypes?.filter(
      (fieldType) => !cardPreset.lockedFieldTypes?.includes(fieldType),
    ),
    allFieldTypesLocked: cardPreset.allFieldTypesLocked ? false : card.allFieldTypesLocked,
    lockedEmailSignatureFields: card.lockedEmailSignatureFields?.filter(
      (field) => !cardPreset.lockedEmailSignatureFields?.includes(field),
    ),
    allEmailSignatureFieldsLocked: cardPreset.allEmailSignatureFieldsLocked
      ? false
      : card.allEmailSignatureFieldsLocked,
    colorLocked: cardPreset.colorLocked ? false : card.colorLocked,
    qrCodeLocked: cardPreset.qrCodeLocked ? false : card.qrCodeLocked,
    layoutImagesLocked: cardPreset.layoutImagesLocked ? false : card.layoutImagesLocked,
    color: fieldsToRemove.has('color') ? DEFAULT_CARD_COLOR : card.color,
    fieldOrder: removeFieldsFromFieldOrder([...fieldsToRemove], card.fieldOrder),
  };

  updatedCard.emailSignature = updatedCard.emailSignature
    ? syncEmailSignature(updatedCard)
    : undefined;
  updatedCard.layout = getSuggestedLayout({
    logo: updatedCard.companyLogoImg?.editedImageUrl,
    cover: updatedCard.coverPhotoImg?.editedImageUrl,
    profile: updatedCard.profilePictureImg?.editedImageUrl,
  });

  return updatedCard;
}

/**
 * Sync up the email signature with the fields currently present in the card. If a field has been
 * removed or cleared then we should remove it from the email signature. This SHOULD NOT be used if
 * you have added any fields to the card (i.e. after applying a preset).
 * @param card The card whose email signature needs to be synced with its fields
 * @returns The in-sync email signature
 */
export function syncEmailSignature(card: Card): Card['emailSignature'] {
  const emailSignature = card.emailSignature;
  return {
    addresses: removeDeletedFieldsFromEmailSignatureField(
      emailSignature?.addresses,
      card.addresses,
    ),
    emails: removeDeletedFieldsFromEmailSignatureField(emailSignature?.emails, card.emails),
    phoneNumbers: removeDeletedFieldsFromEmailSignatureField(
      emailSignature?.phoneNumbers,
      card.phoneNumbers,
    ),
    jobTitle: card.jobTitle ? !!emailSignature?.jobTitle : false,
    orgName: card.orgName ? !!emailSignature?.orgName : false,
    profilePicture: card.profilePictureImg?.editedImageUrl
      ? !!emailSignature?.profilePicture
      : false,
    urls: removeDeletedFieldsFromEmailSignatureField(emailSignature?.urls, card.urls),
    qrCode: true,
    headline: card.headline ? !!emailSignature?.headline : false,
    accreditations:
      card.accreditations && card.accreditations.length > 0
        ? !!emailSignature?.accreditations
        : false,
    companyLogo: card.companyLogoImg?.editedImageUrl ? !!emailSignature?.companyLogo : false,
  };
}

/**
 *
 * @param emailSignatureFields The current options of whether or not to show a field in the email signature
 * @param cardFields The new set of card fields that we want to sync with the email signatures toggles
 * @returns The new set of email signature flags indicating which fields are showing/not showing
 */
export function removeDeletedFieldsFromEmailSignatureField<T, K extends { id?: string }>(
  emailSignatureFields: T | undefined,
  cardFields: K[] | undefined,
): T {
  if (!emailSignatureFields) return {} as T;

  return Object.entries(emailSignatureFields).reduce((emailSignatureFields, [id, val]) => {
    if (!cardFields?.find((field) => field.id === id)) return emailSignatureFields;
    return {
      ...emailSignatureFields,
      [id]: val,
    };
  }, {} as T);
}

/**
 * Create a new card with the provided user as the owner. This uses orgIds and not workspaces so it will
 * need to be redone.
 * @param user User whom will own this new card
 */
export function createNewCard(user: User | AnonymousUser, type: 'personal' | 'team'): Card {
  const card: Partial<Card> = {
    cardId: generateEntityId(),
    cardEditId: generateEntityId(),
    cardTitle: DEFAULT_CARD_TITLE,
    color: DEFAULT_CARD_COLOR,
    creationTimestampMs: Date.now(),
    updatedTimestampMs: Date.now(),
    honorificPrefix: type === 'personal' ? user.honorificPrefix || '' : '',
    firstName: type === 'personal' ? user.firstName || '' : '',
    middleName: type === 'personal' ? user.middleName || '' : '',
    lastName: type === 'personal' ? user.lastName || '' : '',
    honorificSuffix: type === 'personal' ? user.honorificSuffix || '' : '',
    maidenName: type === 'personal' ? user.maidenName || '' : '',
    preferredName: type === 'personal' ? user.preferredName || '' : '',
    pronouns: type === 'personal' ? user.pronouns || '' : '',
    userId: type === 'personal' ? user.userId : undefined,
    referralLinkEnabled: true,
    blinqMessageEnabled: true,
    whereWeMetEnabled: true,
    isInvisible: false,
    isPaused: false,
    orgId: user.bbOrgId,
    profilePictureImg:
      type === 'personal'
        ? { originalImageUrl: user.avatarUrl, editedImageUrl: user.avatarUrl } // set both original and edited.
        : undefined,
    createdBy: type === 'team' ? getName(user) : undefined,
    logoIsInQr: false,
    cardIsEditable: true,
    lockedFields: [],
    lockedFieldTypes: [],
    allFieldTypesLocked: false,
    lockedEmailSignatureFields: [],
    allEmailSignatureFieldsLocked: false,
    colorLocked: false,
    qrCodeLocked: false,
    linkedAttributes: [],
    fieldOrder: [],
  };

  return card as Card;
}

export function createExampleCardFromCache(
  prefilledDataFromCache: Record<string, string> | null,
  fallbacks?: { email?: string; firstName?: string; lastName?: string },
): Card {
  if (
    !prefilledDataFromCache?.email &&
    !prefilledDataFromCache?.phoneNumber &&
    !prefilledDataFromCache?.orgName &&
    !prefilledDataFromCache?.firstName &&
    !prefilledDataFromCache?.lastName &&
    !prefilledDataFromCache?.jobTitle &&
    !fallbacks?.email &&
    !fallbacks?.firstName &&
    !fallbacks?.lastName
  ) {
    // if no data has been cached, then return a blank example card
    return createExampleCard({});
  }
  const email = prefilledDataFromCache?.email ?? fallbacks?.email;

  return createExampleCard({
    emails: email
      ? [
          {
            id: generateFieldID(),
            label: '',
            value: email,
            isPreset: false,
          },
        ]
      : [],
    phoneNumbers: prefilledDataFromCache?.phoneNumber
      ? [
          {
            id: generateFieldID(),
            label: '',
            value: prefilledDataFromCache?.phoneNumber,
            isPreset: false,
          },
        ]
      : [],
    orgName: prefilledDataFromCache?.orgName,
    firstName: prefilledDataFromCache?.firstName ?? fallbacks?.firstName,
    lastName: prefilledDataFromCache?.lastName ?? fallbacks?.lastName,
    jobTitle: prefilledDataFromCache?.jobTitle,
  });
}

export function createExampleCard(cardDetails: Partial<Card>): Card {
  return {
    cardId: generateEntityId(),
    cardEditId: generateEntityId(),
    cardUpdateId: generateEntityId(),
    cardTitle: DEFAULT_CARD_TITLE,
    color: DEFAULT_CARD_COLOR,
    creationTimestampMs: Date.now(),
    updatedTimestampMs: Date.now(),
    referralLinkEnabled: true,
    blinqMessageEnabled: true,
    whereWeMetEnabled: true,
    isInvisible: false,
    isPaused: false,
    layoutImagesLocked: false,
    logoIsInQr: false,
    cardIsEditable: true,
    lockedFields: [],
    lockedFieldTypes: [],
    allFieldTypesLocked: false,
    lockedEmailSignatureFields: [],
    allEmailSignatureFieldsLocked: false,
    colorLocked: false,
    qrCodeLocked: false,
    linkedAttributes: [],
    fieldOrder: populateCardFieldOrder(cardDetails),
    ...cardDetails,
  };
}

/**
 * Create a new card with the provided user as the owner. This uses orgIds and not workspaces so it will
 * need to be redone.
 * @param user User whom will own this new card
 */
export function createNewCardPreset(orgId: string, name: string | undefined): CardPreset {
  return {
    cardPresetId: generateEntityId(),
    cardPresetName: name || DEFAULT_CARD_PRESET_NAME,
    color: DEFAULT_CARD_COLOR,
    presetEditable: true,
    referralLinkEnabled: true,
    blinqMessageEnabled: true,
    whereWeMetEnabled: true,
    isInvisible: false,
    orgId: orgId,
    logoIsInQr: false,
    cardIsEditable: true,
    lockedFields: [],
    layoutImagesLocked: true,
    lockedFieldTypes: [],
    allFieldTypesLocked: false,
    lockedEmailSignatureFields: [],
    allEmailSignatureFieldsLocked: false,
    colorLocked: false,
    qrCodeLocked: false,
    syncUpdates: false,
    fieldOrder: [],
  };
}

/**
 * Update the cardEditId on the card, as well as the updated timestamp.
 * Use this when a new save is being created for the card.
 * @param card The card that we are editing.
 */
export function prepareCardForSave(card: Card): Card {
  if (card.profilePictureImg?.originalImageUrl?.startsWith('blob:')) {
    card.profilePictureImg = null;
  }
  if (card.companyLogoImg?.originalImageUrl?.startsWith('blob:')) {
    card.companyLogoImg = null;
  }
  if (card.coverPhotoImg?.originalImageUrl?.startsWith('blob:')) {
    card.coverPhotoImg = null;
  }

  return {
    ...card,
    cardEditId: generateEntityId(),
    updatedTimestampMs: Date.now(),
  };
}

/**
 * Update the updated timestamp.
 * Use this when a new save is being created for the card preset.
 * @param cardPreset The card preset that we are editing
 */
export function prepareCardPresetForSave(cardPreset: CardPreset): CardPreset {
  return {
    ...cardPreset,
    updatedTimestampMs: Date.now(),
  };
}

/**
 * Creates a mirror copy of a card with new IDs and reset options. We don't need to clone the images since we don't
 * currently update images, for every alteration we create a new image.
 * @param card Card that we wish to duplicate
 */
export function duplicateCard(card: Card, type: 'personal' | 'team', user: User): Card {
  const newCard: Partial<Card> = {
    cardId: generateEntityId(),
    cardEditId: generateEntityId(),
    cardTitle: card.cardTitle ? `${card.cardTitle} #2` : DEFAULT_CARD_TITLE,
    color: card.color,
    creationTimestampMs: Date.now(),
    updatedTimestampMs: Date.now(),
    bbActivationTimestampMs: undefined,
    activationEmailSentAt: undefined,
    firstName: card.firstName,
    lastName: card.lastName,
    userId: type === 'personal' ? user.userId : undefined,
    referralLinkEnabled: card.referralLinkEnabled,
    blinqMessageEnabled: card.blinqMessageEnabled,
    whereWeMetEnabled: card.whereWeMetEnabled,
    isInvisible: false,
    isPaused: false,
    orgId: card.orgId,
    profilePictureImg: card.profilePictureImg,
    companyLogoImg: card.companyLogoImg,
    coverPhotoImg: card.coverPhotoImg,
    createdBy: type === 'team' ? getName(user) : undefined,
    logoIsInQr: card.logoIsInQr,
    lockedFieldTypes: card.lockedFieldTypes,
    allFieldTypesLocked: card.allFieldTypesLocked,
    allEmailSignatureFieldsLocked: card.allEmailSignatureFieldsLocked,
    colorLocked: card.colorLocked,
    qrCodeLocked: card.qrCodeLocked,
    linkedAttributes: [],
    headline: card.headline,
    jobTitle: card.jobTitle,
    logoInQrUrl: card.logoInQrUrl,
    middleName: card.middleName,
    orgName: card.orgName,
    department: card.department,
    honorificPrefix: card.honorificPrefix,
    honorificSuffix: card.honorificSuffix,
    maidenName: card.maidenName,
    preferredName: card.preferredName,
    pronouns: card.pronouns,
    cardPresetId: card.cardPresetId,
    accreditations: card.accreditations,
  };

  const [emailIdsMap, newEmails] = duplicateComplexFields(card.emails);
  const [urlIdsMap, newUrls] = duplicateComplexFields(card.urls);
  const [addressIdsMap, newAddresses] = duplicateComplexFields(card.addresses);
  const [phoneNumberIdsMap, newPhoneNumbers] = duplicateComplexFields(card.phoneNumbers);

  const idsMap = { ...emailIdsMap, ...urlIdsMap, ...addressIdsMap, ...phoneNumberIdsMap };

  const newLockedFields = card.lockedFields.map((id) => {
    return idsMap[id] ?? id;
  });

  const newLockedEmailSignatureFields = card.lockedEmailSignatureFields.map((id) => {
    return idsMap[id] ?? id;
  });

  const newFieldOrder = card.fieldOrder?.map((id) => idsMap[id] ?? id);

  newCard.emails = newEmails;
  newCard.urls = newUrls;
  newCard.addresses = newAddresses;
  newCard.phoneNumbers = newPhoneNumbers;
  newCard.lockedFields = newLockedFields;
  newCard.lockedEmailSignatureFields = newLockedEmailSignatureFields;
  newCard.fieldOrder = newFieldOrder;

  return newCard as Card;
}

/**
 * Duplicates complex card fields, (e.g. URLs, phone numbers, ect), making sure to switch out the old IDs for new IDs
 * @param fields The fields to be duplicated
 */
function duplicateComplexFields<
  T extends {
    id: string;
    label?: string;
    title?: string;
    value: string;
    isPreset: boolean;
    linkedTo?: string | undefined;
  },
>(fields: T[] | undefined): [Record<string, string>, T[] | undefined] {
  if (!fields) return [{}, undefined];
  const idMap: Record<string, string> = {};
  const newFields = [];

  for (const field of fields) {
    const newId = generateEntityId();
    idMap[field.id] = newId;

    newFields.push({ ...field, id: newId });
  }

  return [idMap, newFields];
}

/**
 * Creates a mirror copy of a card preset with new IDs and reset options
 * @param cardPreset Card presetthat we wish to duplicate
 */
export function duplicateCardPreset(cardPreset: CardPreset): CardPreset {
  const newCard = produce(cardPreset, (draftCardPreset) => {
    draftCardPreset.cardPresetId = generateEntityId();
    draftCardPreset.cardPresetName = draftCardPreset.cardPresetName
      ? `${draftCardPreset.cardPresetName} #2`
      : DEFAULT_CARD_PRESET_NAME;
    draftCardPreset.isInvisible = false;
    draftCardPreset.creationTimestampMs = Date.now();
    draftCardPreset.updatedTimestampMs = Date.now();
  });

  return newCard;
}

export function getAllCardPresetFieldIds(cardPreset: CardPreset): string[] {
  const ids = [];
  if (cardPreset.profilePictureImg?.editedImageUrl) ids.push(typeToFieldId('profile'));
  if (cardPreset.companyLogoImg?.editedImageUrl) ids.push(typeToFieldId('logo'));
  if (cardPreset.coverPhotoImg?.editedImageUrl) ids.push(typeToFieldId('cover'));
  if (cardPreset.jobTitle) ids.push('job_title');
  if (cardPreset.orgName) ids.push('org_name');
  if (cardPreset.department) ids.push('department');
  if (cardPreset.accreditations && cardPreset.accreditations.length > 0) ids.push('accreditations');
  if (cardPreset.headline) ids.push('headline');
  cardPreset.urls?.forEach(({ id }) => ids.push(id));
  cardPreset.emails?.forEach(({ id }) => ids.push(id));
  cardPreset.phoneNumbers?.forEach(({ id }) => ids.push(id));
  cardPreset.addresses?.forEach(({ id }) => ids.push(id));

  return ids;
}

export function getAllCardFieldIds(card: Card): string[] {
  const ids = [];
  if (card.profilePictureImg?.editedImageUrl) ids.push(typeToFieldId('profile'));
  if (card.companyLogoImg?.editedImageUrl) ids.push(typeToFieldId('logo'));
  if (card.coverPhotoImg?.editedImageUrl) ids.push(typeToFieldId('profile'));
  if (card.firstName) ids.push('name');
  if (card.jobTitle) ids.push('job_title');
  if (card.orgName) ids.push('org_name');
  if (card.department) ids.push('department');
  if (card.accreditations && card.accreditations.length > 0) ids.push('accreditations');
  if (card.headline) ids.push('headline');
  card.urls?.forEach(({ id }) => ids.push(id));
  card.emails?.forEach(({ id }) => ids.push(id));
  card.phoneNumbers?.forEach(({ id }) => ids.push(id));
  card.addresses?.forEach(({ id }) => ids.push(id));

  return ids;
}

export function getFieldAttributes(fieldId: string): string[] {
  if (fieldId === 'name') {
    return [
      'first_name',
      'middle_name',
      'last_name',
      'honorific_prefix',
      'honorific_suffix',
      'maiden_name',
      'preferred_name',
      'pronouns',
    ];
  } else {
    // In every other case the field maps cleanly to the attribute.
    return [fieldId];
  }
}

export function isOneOfAttributesLinked(linkedAttributes: string[], attributes: string[]): boolean {
  for (const attribute of attributes) {
    if (linkedAttributes.includes(attribute)) return true;
  }

  return false;
}

type CardField =
  | { type: 'email'; value: CardEmail }
  | { type: 'phoneNumber'; value: CardPhoneNumber }
  | { type: 'address'; value: CardAddress }
  | { type: 'url'; value: CardUrl };

type CardPresetField =
  | { type: 'email'; value: CardPresetEmail }
  | { type: 'phoneNumber'; value: CardPresetPhoneNumber }
  | { type: 'address'; value: CardPresetAddress }
  | { type: 'url'; value: CardPresetUrl };

/**
 * Creates a field ordering that contains all the fields in the card. This is needed because the apps do not
 * necessarily add newly created fields to the field order array, which means the fieldOrder can be in a partial
 * state. This function returns it to a complete state.
 * @param card The card whose field order needs to be populated
 */
export function populateCardFieldOrder(
  card: Partial<Pick<Card, 'fieldOrder' | 'phoneNumbers' | 'emails' | 'urls' | 'addresses'>>,
): string[] {
  const orderedFields = createOrderedCardFields(card);
  const fieldOrder = orderedFields.map(({ value }) => value.id);

  return fieldOrder;
}

/**
 * Creates a field ordering that contains all the fields in the card preset. Many older presets do not have a field
 * ordering so this will help populate those instances.
 * @param card The card whose field order needs to be populated
 */
export function populateCardPresetFieldOrder(
  cardPreset: Partial<
    Pick<CardPreset, 'fieldOrder' | 'phoneNumbers' | 'emails' | 'urls' | 'addresses'>
  >,
): string[] {
  const orderedFields = createOrderedCardPresetFields(cardPreset);
  const fieldOrder = orderedFields.map(({ value }) => value.id);

  return fieldOrder;
}

/**
 * Takes a card, and using the fieldOrder creates a combined and ordered collection of the email, phone number,
 * address and url fields
 * @param card The card whose fields need to be ordered
 */
export function createOrderedCardFields(
  card: Partial<Pick<Card, 'fieldOrder' | 'phoneNumbers' | 'emails' | 'urls' | 'addresses'>>,
): CardField[] {
  const fields = combineCardFieldTypes(card);

  if (card.fieldOrder) {
    const sortedFields = sortFields(fields, card.fieldOrder);
    return sortedFields;
  }

  return fields;
}

/**
 * Takes a card preset, and using the fieldOrder creates a combined and ordered collection of the email, phone number,
 * address and url fields
 * @param card The card whose fields need to be ordered
 */
export function createOrderedCardPresetFields(
  cardPreset: Partial<
    Pick<CardPreset, 'fieldOrder' | 'phoneNumbers' | 'emails' | 'urls' | 'addresses'>
  >,
): CardPresetField[] {
  const fields = combineCardPresetFieldTypes(cardPreset);

  if (cardPreset.fieldOrder) {
    const sortedFields = sortFields(fields, cardPreset.fieldOrder);
    return sortedFields;
  }

  return fields;
}

/**
 * Given the collection of all fields that can be ordered (e.g. URLs, emails, ect) and a field ordering
 * return the ordered collection of fields. Fields not present in the ordering will be placed after the ordered
 * fields in their intrinsic order.
 * @param fields The collection of all fields that can be ordered
 * @param fieldOrder The field ordering we would like to apply
 */
function sortFields<T extends { value: MultiValuedField }>(fields: T[], fieldOrder: string[]): T[] {
  const sortedFields = [...fields];

  sortedFields.sort((field1, field2) => {
    const field1Order = fieldOrder.findIndex((fieldId) => fieldId === field1.value.id);
    const field2Order = fieldOrder.findIndex((fieldId) => fieldId === field2.value.id);

    if (field1Order !== -1 && field2Order === -1) return -1;
    if (field1Order === -1 && field2Order !== -1) return 1;
    if (field1Order === -1 && field2Order !== -1) return 0;
    return field1Order - field2Order;
  });

  return sortedFields;
}

/**
 * Inserts a field into the field order by following these rules:
 * 1. If another field of the same type exists then it should be added after the last instance of this field type
 * 2. If no other field of the same type exists then it should be appended to the end of the field order
 *
 * @param cardPreset The card preset whose field order is to be modified
 * @param fieldIdToInsert The field ID to insert into the field order
 * @param isSameType A callback that should return true if the field is of the same type
 * @returns The new field order
 */
export function insertFieldOfTypeInCardPresetFieldOrder(
  cardPreset: CardPresetFields,
  fieldIdToInsert: string,
  isSameType: (field: CardPresetField) => boolean,
): string[] {
  const fields = combineCardPresetFieldTypes(cardPreset);

  const newFieldOrder = cardPreset.fieldOrder ? [...cardPreset.fieldOrder] : [];

  /**
   * Function used to determine the order of the elements. It is expected to return a negative value if first argument is less than second argument, zero if they're equal and a positive value otherwise. If omitted, the elements are sorted in ascending, ASCII character order.
   */
  if (cardPreset.fieldOrder) {
    const fieldOrderHasFieldOfSameType = newFieldOrder.some((id) => {
      const field = fields.find((field) => field.value.id === id);

      if (!field) return false;

      return isSameType(field);
    });

    if (fieldOrderHasFieldOfSameType) {
      // Get the index that is one after the last instance of the same field type
      const indexToInsertAt =
        newFieldOrder.length -
        [...newFieldOrder].reverse().findIndex((id) => {
          const field = fields.find((field) => field.value.id === id);

          if (!field) return false;

          return isSameType(field);
        });

      newFieldOrder.splice(indexToInsertAt, 0, fieldIdToInsert);
    } else {
      newFieldOrder.push(fieldIdToInsert);
    }
  }

  return newFieldOrder;
}

/**
 * Inserts a field into the field order by following these rules:
 * 1. If preset fields exists then it should be added before any preset fields
 * 2. If no preset fields exist then it should be appended to the end of the field order
 *
 * This may still have strange results if you have jumbled up the preset fields and card fields however this
 * is more of an edge case and it is still a decent assumption to make that a card field should be placed before
 * any preset field since it is more specific to the card owner.
 * @param card The card whose field order is to be modified
 * @param fieldIdToInsert The field ID to insert into the field order
 * @returns The new field order
 */
export function insertFieldOfTypeInCardFieldOrder(
  card: Omit<CardFields, 'linkedAttributes'>,
  fieldIdToInsert: string,
): string[] {
  const fields = combineCardFieldTypes(card);

  const newFieldOrder = card.fieldOrder ? [...card.fieldOrder] : [];

  /**
   * Function used to determine the order of the elements. It is expected to return a negative value if first argument is less than second argument, zero if they're equal and a positive value otherwise. If omitted, the elements are sorted in ascending, ASCII character order.
   */
  if (card.fieldOrder) {
    const firstPresetFieldIndex = newFieldOrder.findIndex((id) => {
      const field = fields.find((field) => field.value.id === id);

      if (!field) return false;

      return field.value.isPreset;
    });

    if (firstPresetFieldIndex !== -1) {
      // Insert the field before the first instance of a preset field
      newFieldOrder.splice(firstPresetFieldIndex, 0, fieldIdToInsert);
    } else {
      // If there are no preset fields and no fields of the same type then insert the
      // field at the end of the field order
      newFieldOrder.push(fieldIdToInsert);
    }
  }

  return newFieldOrder;
}

/**
 * Removes a set of fields from the field order
 * @param fieldsToRemove The IDs of the fields to be removed
 * @param fieldOrder The current field ordering
 */
function removeFieldsFromFieldOrder(fieldsToRemove: string[], fieldOrder: string[]): string[] {
  return fieldOrder.filter((id) => {
    return !fieldsToRemove.some((idToRemove) => id === idToRemove);
  });
}

/**
 * Combines the email, phone number, address and url fields from a given card into a single collection
 * in their intrinsic order (phone number, emails, urls, address)
 * @param card The card whose fields need to be combined
 */
function combineCardFieldTypes(
  card: Pick<Card, 'phoneNumbers' | 'emails' | 'urls' | 'addresses'>,
): CardField[] {
  const fields = [];

  // The order we push here is important, should always be email, phone number, url then address
  if (card.emails) {
    for (const email of card.emails) {
      fields.push({ type: 'email' as const, value: email });
    }
  }

  if (card.phoneNumbers) {
    for (const phoneNumber of card.phoneNumbers) {
      fields.push({ type: 'phoneNumber' as const, value: phoneNumber });
    }
  }

  if (card.urls) {
    for (const url of card.urls) {
      fields.push({ type: 'url' as const, value: url });
    }
  }

  if (card.addresses) {
    for (const address of card.addresses) {
      fields.push({ type: 'address' as const, value: address });
    }
  }

  return fields;
}

/**
 * Combines the email, phone number, address and url fields from a given card preset into a single collection
 * in their intrinsic order (phone number, emails, urls, address)
 * @param cardPreset The card preset whose fields need to be combined
 */
function combineCardPresetFieldTypes(
  cardPreset: Pick<CardPreset, 'phoneNumbers' | 'emails' | 'urls' | 'addresses'>,
): CardPresetField[] {
  const fields = [];

  // The order we push here is important, should always be email, phone number, url then address
  if (cardPreset.emails) {
    for (const email of cardPreset.emails) {
      fields.push({ type: 'email' as const, value: email });
    }
  }

  if (cardPreset.phoneNumbers) {
    for (const phoneNumber of cardPreset.phoneNumbers) {
      fields.push({ type: 'phoneNumber' as const, value: phoneNumber });
    }
  }

  if (cardPreset.urls) {
    for (const url of cardPreset.urls) {
      fields.push({ type: 'url' as const, value: url });
    }
  }

  if (cardPreset.addresses) {
    for (const address of cardPreset.addresses) {
      fields.push({ type: 'address' as const, value: address });
    }
  }

  return fields;
}

/**
 * A collection of URLs types for which we support auto detect.
 */
const DETECTABLE_URL_FIELD_TYPES: CardUrlType[] = [
  'calendly',
  'cashapp',
  'discord',
  'facebook',
  'github',
  'instagram',
  'linkedin',
  'paypal',
  'skype',
  'snapchat',
  'telegram',
  'tiktok',
  'twitch',
  'twitter',
  'venmo',
  'yelp',
  'youtube',
];

/**
 * Finds the first partial or complete match for the specified value and returns the URL type.
 */
export function findPartialOrCompleteType(value: string): CardUrlType | undefined {
  return DETECTABLE_URL_FIELD_TYPES.find((urlFieldType) => {
    const state = getURLFieldValueState(urlFieldType, value);
    return state === 'complete' || state === 'partial';
  });
}

/**
 * Removes the default value if a valid URL is entered directly after it.
 * This helps us handle the case where a user quickly pastes in a URL
 * without first removing the default value.
 * @param word The text provided in the URL Edit Popover for the value.
 * @param urlType The type of URL the user has selected.
 * @example // returns https://twitter.com/hey
autocorrect("@https://twitter.com/hey", "twitter");
 * @example // returns https://cash.app/$hey
autocorrect("$https://cash.app/$hey", "cashapp");
 */
export function autocorrectURLValue(word: string, urlType: CardUrlType): string {
  const prefix: string = getDefaultUrlValue(urlType);
  const re = new RegExp(`^${escapeRegExp(prefix)}(.*)$`);
  const match = word.match(re);
  if (match) {
    if (isValidUrl(match[1])) {
      return match[1];
    }
  }
  return word;
}

/**
 * A shorthand to escape specific regex values.
 */
function escapeRegExp(s: string | undefined): string | undefined {
  return s?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}
